/**
 * Pricing Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TableDataSource,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseSpacingFormat,
  parseTableDataSource,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatSpacingFormat,
  formatTableDataSource,
  formatWidthFormat,
  formatResponsiveValue,
} from '../../format-utils'
import pricingTemplate from './pricing.template.json'

export interface IPricingComponentSettings {
  // Data settings
  'pricing-data-source': string
  'pricing-data-name': string
  'pricing-data-description': string
  'pricing-data-monthly-price': string
  'pricing-data-yearly-price': string
  'pricing-data-yearly-discount': string
  'pricing-data-badge-text': VariableTextValue
  'pricing-data-button-text': string
  'pricing-data-popular': string
  'pricing-data-default': string
  'pricing-data-features-source': TableDataSource
  'pricing-data-monthly-discount': string

  // Design settings
  'pricing-design-accent-color': ColorAdaptiveValue<string>
  'pricing-design-billing-cycle': string
  'pricing-design-billing-toggle-active-background': ColorAdaptiveValue<string>
  'pricing-design-billing-toggle-alignment': ResponsiveValue<string>
  'pricing-design-billing-toggle-background': ColorAdaptiveValue<string>
  'pricing-design-billing-toggle-discount-background': ColorAdaptiveValue<string>
  'pricing-design-border': ColorAdaptiveValue<string>
  'pricing-design-button-primary': ColorAdaptiveValue<string>
  'pricing-design-button-secondary': string
  'pricing-design-card-background-end-gradient': ColorAdaptiveValue<string>
  'pricing-design-card-background-gradient-direction': string
  'pricing-design-card-background-solid': ColorAdaptiveValue<string>
  'pricing-design-card-background-start-gradient': ColorAdaptiveValue<string>
  'pricing-design-card-background-type': string
  'pricing-design-card-badge-background': ColorAdaptiveValue<string>
  'pricing-design-card-border': ColorAdaptiveValue<string>
  'pricing-design-card-button-primary': ColorAdaptiveValue<string>
  'pricing-design-card-button-secondary': ColorAdaptiveValue<string>
  'pricing-design-card-popular-border': ColorAdaptiveValue<string>
  'pricing-design-card-popular-ring': ColorAdaptiveValue<string>
  'pricing-design-column-background-opacity': ColorAdaptiveValue<string>
  'pricing-design-even-row-background': ColorAdaptiveValue<string>
  'pricing-design-gap': string
  'pricing-design-header-background': ColorAdaptiveValue<string>
  'pricing-design-header-border': ColorAdaptiveValue<string>
  'pricing-design-icon-error': ColorAdaptiveValue<string>
  'pricing-design-icon-success': ColorAdaptiveValue<string>
  'pricing-design-layout': ResponsiveValue<string>
  'pricing-design-layout-alignment': string
  'pricing-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'pricing-design-odd-row-background': ColorAdaptiveValue<string>
  'pricing-design-popular-badge-background': ColorAdaptiveValue<string>
  'pricing-design-popular-badge-text': ColorAdaptiveValue<string>
  'pricing-design-show-badges': boolean
  'pricing-design-show-billing-toggle': boolean
  'pricing-design-show-button': boolean
  'pricing-design-show-popular': boolean
  'pricing-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>

  // Actions
  'pricing-action-on-click-select-plan': BuilderActions
}

export interface IPricingComponent extends IBuilderComponent {
  componentId: 'pricing'
  groupId: 'app'
  settings: IPricingComponentSettings
}

/**
 * Pricing AI Input
 */
export interface AiPricingInput {
  dataSource: string // Table ID
  nameColumn?: string // Column ID for plan name
  descriptionColumn?: string // Column ID for description
  monthlyPriceColumn?: string // Column ID for monthly price
  yearlyPriceColumn?: string // Column ID for yearly price
  yearlyDiscountColumn?: string // Column ID for yearly discount
  monthlyDiscountColumn?: string // Column ID for monthly discount
  badgeTextColumn?: string // Column ID for badge text
  buttonTextColumn?: string // Column ID for button text
  popularColumn?: string // Column ID for popular flag
  defaultColumn?: string // Column ID for default flag
  featuresSource?: any // Features table data source (can use parseTableDataSource)
  width?: string // Format: "w:100%"
  margin?: string // Format: "m:10px"
  gap?: string // Format: "large", "medium", "small"
  layout?: string // Format: "table" or "card" or "table ta:card de:table"
  layoutAlignment?: string // Format: "center" or "left"
  billingCycle?: string // Format: "monthly" or "yearly"
  billingToggleAlignment?: string // Format: "center" or "left"
  showBadges?: boolean // Show badges
  showBillingToggle?: boolean // Show billing toggle
  showButton?: boolean // Show button
  showPopular?: boolean // Show popular badge
  actionOnClickSelectPlan?: BuilderActions
}

const PRICING_DEFAULT_TEMPLATE = pricingTemplate as unknown as IPricingComponentSettings

export class PricingComp {
  getTemplate(): IPricingComponentSettings {
    return PRICING_DEFAULT_TEMPLATE
  }

  /**
   * format pricing component settings to AI pricing input
   */
  format(settings: IPricingComponentSettings): AiPricingInput {
    const result: AiPricingInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['pricing-data-source']) {
      result.dataSource = settings['pricing-data-source']
    }

    // Data - Columns
    if (settings['pricing-data-name']) {
      result.nameColumn = settings['pricing-data-name']
    }

    if (settings['pricing-data-description']) {
      result.descriptionColumn = settings['pricing-data-description']
    }

    if (settings['pricing-data-monthly-price']) {
      result.monthlyPriceColumn = settings['pricing-data-monthly-price']
    }

    if (settings['pricing-data-yearly-price']) {
      result.yearlyPriceColumn = settings['pricing-data-yearly-price']
    }

    if (settings['pricing-data-yearly-discount']) {
      result.yearlyDiscountColumn = settings['pricing-data-yearly-discount']
    }

    if (settings['pricing-data-monthly-discount']) {
      result.monthlyDiscountColumn = settings['pricing-data-monthly-discount']
    }

    if (settings['pricing-data-badge-text']) {
      const badgeText = formatFromVariableText(settings['pricing-data-badge-text'])
      result.badgeTextColumn = badgeText.length > 0 ? badgeText.join(' ') : undefined
    }

    if (settings['pricing-data-button-text']) {
      result.buttonTextColumn = settings['pricing-data-button-text']
    }

    if (settings['pricing-data-popular']) {
      result.popularColumn = settings['pricing-data-popular']
    }

    if (settings['pricing-data-default']) {
      result.defaultColumn = settings['pricing-data-default']
    }

    // Data - Features Source
    if (settings['pricing-data-features-source']) {
      result.featuresSource = formatTableDataSource(settings['pricing-data-features-source'])
    }

    // Design - Width (handle OverflowSettings)
    if (settings['pricing-design-width']) {
      const width = formatWidthFormat(settings['pricing-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['pricing-design-margin']) {
      const margin = formatSpacingFormat(settings['pricing-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Gap
    if (settings['pricing-design-gap']) {
      result.gap = settings['pricing-design-gap']
    }

    // Design - Layout
    if (settings['pricing-design-layout']) {
      result.layout = formatResponsiveValue(settings['pricing-design-layout'], (val) => String(val))
    }

    // Design - Layout Alignment
    if (settings['pricing-design-layout-alignment']) {
      result.layoutAlignment = settings['pricing-design-layout-alignment']
    }

    // Design - Billing Cycle
    if (settings['pricing-design-billing-cycle']) {
      result.billingCycle = settings['pricing-design-billing-cycle']
    }

    // Design - Billing Toggle Alignment
    if (settings['pricing-design-billing-toggle-alignment']) {
      const alignment = formatResponsiveValue(settings['pricing-design-billing-toggle-alignment'], (val) => String(val))
      // Extract first value if all are the same
      const values = Object.values(settings['pricing-design-billing-toggle-alignment'])
      if (values.every((v) => v === values[0])) {
        result.billingToggleAlignment = values[0]
      } else {
        result.billingToggleAlignment = alignment
      }
    }

    // Design - Show Flags
    if (settings['pricing-design-show-badges'] !== undefined) {
      result.showBadges = settings['pricing-design-show-badges']
    }

    if (settings['pricing-design-show-billing-toggle'] !== undefined) {
      result.showBillingToggle = settings['pricing-design-show-billing-toggle']
    }

    if (settings['pricing-design-show-button'] !== undefined) {
      result.showButton = settings['pricing-design-show-button']
    }

    if (settings['pricing-design-show-popular'] !== undefined) {
      result.showPopular = settings['pricing-design-show-popular']
    }

    return result
  }

  /**
   * parse AI pricing input to pricing component settings
   */
  parse(input: AiPricingInput): IPricingComponentSettings {
    const result = deepMerge(PRICING_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['pricing-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.nameColumn) {
      result['pricing-data-name'] = input.nameColumn
    }

    if (input.descriptionColumn) {
      result['pricing-data-description'] = input.descriptionColumn
    }

    if (input.monthlyPriceColumn) {
      result['pricing-data-monthly-price'] = input.monthlyPriceColumn
    }

    if (input.yearlyPriceColumn) {
      result['pricing-data-yearly-price'] = input.yearlyPriceColumn
    }

    if (input.yearlyDiscountColumn) {
      result['pricing-data-yearly-discount'] = input.yearlyDiscountColumn
    }

    if (input.monthlyDiscountColumn) {
      result['pricing-data-monthly-discount'] = input.monthlyDiscountColumn
    }

    if (input.badgeTextColumn) {
      result['pricing-data-badge-text'] = parseToVariableText(input.badgeTextColumn)
    }

    if (input.buttonTextColumn) {
      result['pricing-data-button-text'] = input.buttonTextColumn
    }

    if (input.popularColumn) {
      result['pricing-data-popular'] = input.popularColumn
    }

    if (input.defaultColumn) {
      result['pricing-data-default'] = input.defaultColumn
    }

    // Data - Features Source (table data source)
    if (input.featuresSource) {
      result['pricing-data-features-source'] = parseTableDataSource<TableDataSource>(input.featuresSource, {
        tableKey: 'table',
        includeIcon: true,
        isDetailPageTable: false,
      })
    }

    // Design - Width
    if (input.width) {
      result['pricing-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Margin
    if (input.margin) {
      result['pricing-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Gap
    if (input.gap) {
      result['pricing-design-gap'] = input.gap
    }

    // Design - Layout
    if (input.layout) {
      const layoutMap: Record<string, string> = {
        table: 'table',
        card: 'card',
      }
      const parts = input.layout.split(/\s+/)
      const layoutObj: ResponsiveValue<string> = {
        phone: 'table',
        'phone-landscape': 'table',
        tablet: 'table',
        'tablet-landscape': 'table',
        desktop: 'table',
        tv: 'table',
      }

      for (const part of parts) {
        if (part.includes(':')) {
          const [breakpoint, layoutValue] = part.split(':')
          const bpMap: Record<string, keyof ResponsiveValue<string>> = {
            p: 'phone',
            pl: 'phone-landscape',
            t: 'tablet',
            tl: 'tablet-landscape',
            de: 'desktop',
            tv: 'tv',
          }
          const bp = bpMap[breakpoint]
          if (bp && layoutMap[layoutValue]) {
            layoutObj[bp] = layoutMap[layoutValue]
          }
        } else if (layoutMap[part]) {
          Object.keys(layoutObj).forEach((key) => {
            layoutObj[key as keyof ResponsiveValue<string>] = layoutMap[part]
          })
        }
      }

      result['pricing-design-layout'] = layoutObj
    }

    // Design - Layout Alignment
    if (input.layoutAlignment) {
      result['pricing-design-layout-alignment'] = input.layoutAlignment
    }

    // Design - Billing Cycle
    if (input.billingCycle) {
      result['pricing-design-billing-cycle'] = input.billingCycle
    }

    // Design - Billing Toggle Alignment
    if (input.billingToggleAlignment) {
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
      }
      const alignment = alignmentMap[input.billingToggleAlignment] || 'center'
      result['pricing-design-billing-toggle-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      }
    }

    // Design - Show Flags
    if (input.showBadges !== undefined) {
      result['pricing-design-show-badges'] = input.showBadges
    }

    if (input.showBillingToggle !== undefined) {
      result['pricing-design-show-billing-toggle'] = input.showBillingToggle
    }

    if (input.showButton !== undefined) {
      result['pricing-design-show-button'] = input.showButton
    }

    if (input.showPopular !== undefined) {
      result['pricing-design-show-popular'] = input.showPopular
    }

    // Actions
    if (input.actionOnClickSelectPlan) {
      result['pricing-action-on-click-select-plan'] = input.actionOnClickSelectPlan
    }

    return result
  }
}

